import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import { CameraIcon, TrashIcon, LinkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import apiClient from '../utils/api';
import API_CONFIG from '../config/api';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import { useDeviceStatus } from '../hooks/useDeviceStatus';
import { useTheme } from '../hooks/useTheme';
import { formatDateTime } from '../utils/dateFormatter';
import { TimezoneCombobox } from '../components/TimezonePicker';
import ReminderSettings from '../components/settings/ReminderSettings';
import ChecklistEditor from '../components/settings/ChecklistEditor';
import MeSectionNav, { ME_SECTIONS } from '../components/settings/MeSectionNav';
import HealthConnectRow from '../components/settings/HealthConnectRow';

// Height/weight are STORED in metric (cm/kg) so the twin's seed ranges and
// targets stay unit-consistent; we convert only for display and entry in the
// user's preferred unit. These helpers take an `imperial` flag (lb/in).
const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const r1 = (n) => Math.round(n * 10) / 10;
const r2 = (n) => Math.round(n * 100) / 100;
const kgToDisplay = (kg, imperial) => (kg === '' || kg == null ? '' : r1(imperial ? kg / KG_PER_LB : kg));
const cmToDisplay = (cm, imperial) => (cm === '' || cm == null ? '' : r1(imperial ? cm / CM_PER_IN : cm));
const displayToKg = (v, imperial) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : r2(imperial ? n * KG_PER_LB : n);
};
const displayToCm = (v, imperial) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : r1(imperial ? n * CM_PER_IN : n);
};

const MePage = () => {
  const { user, refreshUser, loading: authLoading, logout, deleteAccount } = useAuth();
  const fileInputRef = useRef(null);
  // Account deletion (irreversible). Password accounts must re-auth; social-only
  // accounts (no password) confirm with the typed phrase alone.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const deleteRequiresPassword = !!user?.has_password;
  const canConfirmDelete =
    deleteConfirmText.trim() === 'DELETE' &&
    (!deleteRequiresPassword || deletePassword.length > 0) &&
    !deleting;

  const closeDeleteModal = () => {
    if (deleting) return; // don't let a mid-flight delete be dismissed
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeletePassword('');
  };

  const handleDeleteAccount = async () => {
    if (!canConfirmDelete) return;
    setDeleting(true);
    try {
      await deleteAccount({
        confirmation: deleteConfirmText.trim(),
        password: deleteRequiresPassword ? deletePassword : undefined,
      });
      // deleteAccount() cleared auth state; the app's route guard now sends us
      // to the login screen. This component unmounts, so no further setState.
      toast.success('Your account and all data have been deleted.');
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Could not delete account. Please try again.';
      toast.error(detail);
      setDeleting(false);
    }
  };
  const [uploading, setUploading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { fitbitStatus, loading: loadingFitbit, refetch: refetchFitbitStatus } = useFitbitStatus();
  const { deviceStatus: ouraStatus, loading: loadingOura, refetch: refetchOura } = useDeviceStatus('oura');
  const { deviceStatus: withingsStatus, loading: loadingWithings, refetch: refetchWithings } = useDeviceStatus('withings');
  const { deviceStatus: whoopStatus, loading: loadingWhoop, refetch: refetchWhoop } = useDeviceStatus('whoop');
  const [disconnectingDevice, setDisconnectingDevice] = useState(null);
  const [syncingDevice, setSyncingDevice] = useState(null);
  const [syncingFitbit, setSyncingFitbit] = useState(false);
  // Providers the user has asked us to support (for the "Request support" state on
  // devices we can't service yet, e.g. WHOOP without a registered app).
  const [requestedProviders, setRequestedProviders] = useState([]);
  const { theme, toggleTheme } = useTheme();


  // Personal stats state
  const [statsActiveTab, setStatsActiveTab] = useState('demographics');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSaving, setStatsSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    age: '', sex: '', height: '', weight: '', ethnicity: '', location: '',
    chronic_conditions: [], medications: [], allergies: [],
    dietary_preferences: '', activity_level: '', sleep_hours: '',
    primary_goal: '',
    mood_logs: [],
  });

  const statsTabs = [
    { id: 'demographics', label: 'Demographics' },
    { id: 'medical', label: 'Medical' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'goals', label: 'Goals' },
    { id: 'mental-health', label: 'Mental Health' },
  ];

  // ── Profile helpers ──

  const formattedCreatedAt = useMemo(() => {
    return user?.created_at ? formatDateTime(user.created_at) : '—';
  }, [user?.created_at]);

  const getProfilePictureUrl = useMemo(() => {
    if (user?.profile_picture) {
      return `${API_CONFIG.UPLOADS_BASE_URL}/profile_pictures/${user.profile_picture}`;
    }
    return null;
  }, [user?.profile_picture]);

  const validateRedirectUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const allowedDomains = ['www.fitbit.com', 'accounts.fitbit.com'];
      return allowedDomains.includes(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const handleConnectFitbit = async () => {
    try {
      const response = await apiClient.get('/api/auth/fitbit/connect');
      if (response.status === 200) {
        const redirectUrl = response.data.redirect_url;
        if (!validateRedirectUrl(redirectUrl)) {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        window.location.href = redirectUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Fitbit connection');
    }
  };

  const handleSyncFitbit = async () => {
    setSyncingFitbit(true);
    try {
      await apiClient.post('/api/fitbit/sync', {});
      toast.success('Fitbit synced');
      await refetchFitbitStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Couldn’t sync Fitbit just now — please try again.');
    } finally {
      setSyncingFitbit(false);
    }
  };

  const handleDisconnectFitbit = async () => {
    setDisconnecting(true);
    try {
      const response = await apiClient.post('/api/auth/fitbit/disconnect', {});
      if (response.status === 200) {
        toast.success('Fitbit account disconnected successfully');
        await refetchFitbitStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect Fitbit account');
    } finally {
      setDisconnecting(false);
    }
  };

  // Generic device-layer connect/disconnect (Oura and future providers).
  // Fitbit keeps its own handlers above until it's migrated onto this layer.
  const DEVICE_ALLOWED_HOSTS = {
    oura: ['cloud.ouraring.com'],
    withings: ['account.withings.com'],
    whoop: ['api.prod.whoop.com'],
  };

  const handleConnectDevice = async (provider) => {
    try {
      const response = await apiClient.get(`/api/auth/device/${provider}/connect`);
      if (response.status === 200) {
        const redirectUrl = response.data.redirect_url;
        const allowed = DEVICE_ALLOWED_HOSTS[provider] || [];
        let host;
        try {
          host = new URL(redirectUrl).hostname;
        } catch {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        if (!allowed.includes(host)) {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        window.location.href = redirectUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to initiate ${provider} connection`);
    }
  };

  const handleSyncDevice = async (provider, refetch) => {
    setSyncingDevice(provider);
    try {
      const res = await apiClient.post(`/api/auth/device/${provider}/sync`, {});
      const results = res.data?.results || {};
      // Sum any {synced: n} counts the adapter returned into a friendly total.
      const total = Object.values(results).reduce(
        (n, v) => n + (v && typeof v.synced === 'number' ? v.synced : 0), 0);
      toast.success(total > 0 ? `Synced — ${total} update${total === 1 ? '' : 's'}` : 'Synced — already up to date');
      await refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Couldn't sync ${provider}`);
    } finally {
      setSyncingDevice(null);
    }
  };

  const handleDisconnectDevice = async (provider, refetch) => {
    setDisconnectingDevice(provider);
    try {
      const response = await apiClient.post(`/api/auth/device/${provider}/disconnect`, {});
      if (response.status === 200) {
        toast.success('Device disconnected successfully');
        await refetch();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to disconnect ${provider}`);
    } finally {
      setDisconnectingDevice(null);
    }
  };

  useEffect(() => {
    apiClient.get('/api/device-interest/mine')
      .then((r) => setRequestedProviders(r.data?.requested || []))
      .catch(() => {});
  }, []);

  const handleRequestSupport = async (provider) => {
    // Optimistic: a demand signal should never feel like it failed.
    setRequestedProviders((prev) => (prev.includes(provider) ? prev : [...prev, provider]));
    try {
      await apiClient.post(`/api/device-interest/${provider}`);
      toast.success("Thanks — we'll let you know when it's supported.");
    } catch (error) {
      toast.error('Could not record your request');
    }
  };

  const formatConnectedDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  // The generic device-layer providers (Fitbit keeps its own legacy row above).
  const genericDevices = [
    { key: 'oura', label: 'Oura Ring', desc: 'Sleep, HRV & recovery',
      status: ouraStatus, loading: loadingOura, refetch: refetchOura },
    { key: 'withings', label: 'Withings', desc: 'Scale, body composition & blood pressure',
      status: withingsStatus, loading: loadingWithings, refetch: refetchWithings },
    { key: 'whoop', label: 'WHOOP', desc: 'Recovery, HRV & sleep',
      status: whoopStatus, loading: loadingWhoop, refetch: refetchWhoop },
  ];

  // Devices we can't OAuth-connect (yet) but people own and ask for. They only
  // take a demand signal — no status call, always a Request-support action.
  // Garmin = closed partner API; Apple Watch = HealthKit (needs the native app);
  // Pixel / Wear OS = Health Connect (Android app; see the Health Connect row).
  const requestableDevices = [
    { key: 'garmin', label: 'Garmin', desc: 'Watches & cycling — activity, sleep & HRV' },
    { key: 'apple_watch', label: 'Apple Watch', desc: 'Activity, workouts & heart rate' },
    { key: 'pixel_watch', label: 'Pixel / Wear OS', desc: 'Activity & sleep via Health Connect' },
  ];

  // Right-side action per device: Disconnect / Connect when we can service it, or
  // "Request support" when the provider isn't configured on the server yet.
  const renderDeviceAction = (provider, status, loading, refetch) => {
    if (loading) return <Button variant="outline" size="sm" disabled>Loading...</Button>;
    if (status.connected) {
      // Stack the two actions vertically so a connected card never pushes Disconnect
      // off the right edge — the card has the vertical room and doesn't grow wider.
      return (
        <div className="flex flex-col items-stretch gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => handleSyncDevice(provider, refetch)}
            disabled={syncingDevice === provider}
          >
            {syncingDevice === provider ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => handleDisconnectDevice(provider, refetch)}
            disabled={disconnectingDevice === provider}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            {disconnectingDevice === provider ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </div>
      );
    }
    if (status.configured) {
      return <Button variant="outline" size="sm" onClick={() => handleConnectDevice(provider)}>Connect</Button>;
    }
    return requestedProviders.includes(provider)
      ? <Button variant="outline" size="sm" disabled>Requested ✓</Button>
      : <Button variant="outline" size="sm" onClick={() => handleRequestSupport(provider)}>Request support</Button>;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await apiClient.post('/api/auth/profile-picture', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status === 200) {
        toast.success('Profile picture uploaded!');
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      const response = await apiClient.delete('/api/auth/profile-picture');
      if (response.status === 200) {
        toast.success('Profile picture deleted!');
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile picture');
    }
  };

  // ── Units ──
  // formData.height/weight hold DISPLAY values in the current
  // unit; a ref carries the unit into the []-dep fetch callback without stale
  // closures, and toggling reinterprets the in-box numbers into the new unit.
  const isImperial = (user?.weight_unit || 'kg') === 'lb';
  const weightUnitLabel = isImperial ? 'lb' : 'kg';
  const heightUnitLabel = isImperial ? 'in' : 'cm';
  const unitRef = useRef(user?.weight_unit || 'kg');
  useEffect(() => {
    unitRef.current = user?.weight_unit || 'kg';
  }, [user?.weight_unit]);

  const handleWeightUnitChange = async (newUnit) => {
    const cur = user?.weight_unit || 'kg';
    if (newUnit === cur) return;
    const curImp = cur === 'lb';
    const newImp = newUnit === 'lb';
    // Reinterpret the in-box numbers into the new unit (round-trip via metric).
    setFormData((prev) => ({
      ...prev,
      height: cmToDisplay(displayToCm(prev.height, curImp), newImp),
      weight: kgToDisplay(displayToKg(prev.weight, curImp), newImp),
    }));
    unitRef.current = newUnit;
    try {
      await apiClient.patch('/api/auth/weight-unit', { weight_unit: newUnit });
      await refreshUser();
    } catch {
      toast.error('Failed to update weight unit');
    }
  };

  // ── Personal stats helpers ──

  const fetchPersonalStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/api/personal-stats/');
      if (response.status === 200) {
        const data = response.data;
        setStats(data);
        const imp = unitRef.current === 'lb';
        setFormData({
          age: data.age || '', sex: data.sex || '',
          height: cmToDisplay(data.height, imp), weight: kgToDisplay(data.weight, imp),
          ethnicity: data.ethnicity || '', location: data.location || '',
          chronic_conditions: data.chronic_conditions || [],
          medications: data.medications || [],
          allergies: data.allergies || [],
          dietary_preferences: data.dietary_preferences || '',
          activity_level: data.activity_level || '',
          sleep_hours: data.sleep_hours || '',
          primary_goal: data.primary_goal || '',
          mood_logs: data.mood_logs || [],
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch personal stats');
      }
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPersonalStats();
  }, [fetchPersonalStats]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field) => {
    setFormData((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const addMoodLog = () => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: [...prev.mood_logs, { date: '', mood: '' }],
    }));
  };

  const handleMoodLogChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: prev.mood_logs.map((log, i) =>
        i === index ? { ...log, [field]: value } : log
      ),
    }));
  };

  const removeMoodLog = (index) => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: prev.mood_logs.filter((_, i) => i !== index),
    }));
  };

  const handleStatsSubmit = async (e) => {
    e.preventDefault();
    // No required fields — a partial profile is allowed and still improves the
    // twin. Empty numeric fields save as null; height/weight convert to metric.
    setStatsSaving(true);
    const imp = unitRef.current === 'lb';
    try {
      const method = stats ? 'put' : 'post';
      const response = await apiClient[method]('/api/personal-stats/', {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        height: displayToCm(formData.height, imp),
        weight: displayToKg(formData.weight, imp),
        sleep_hours: formData.sleep_hours ? parseFloat(formData.sleep_hours) : null,
        chronic_conditions: formData.chronic_conditions.filter((item) => item.trim()),
        medications: formData.medications.filter((item) => item.trim()),
        allergies: formData.allergies.filter((item) => item.trim()),
        mood_logs: formData.mood_logs.filter((log) => log.date && log.mood),
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(stats ? 'Personal stats updated!' : 'Personal stats created!');
        await fetchPersonalStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save personal stats');
    } finally {
      setStatsSaving(false);
    }
  };

  // ── Render helpers for stats tabs ──

  const renderUnitToggle = () => (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Units</span>
      <div className="flex gap-2">
        {[['kg', 'kg / cm'], ['lb', 'lb / in']].map(([u, lbl]) => (
          <button
            type="button"
            key={u}
            onClick={() => handleWeightUnitChange(u)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
              (user?.weight_unit || 'kg') === u
                ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );

  const renderDemographicsTab = () => (
    <div className="space-y-6">
      {renderUnitToggle()}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Age</label>
          <Input type="number" value={formData.age} onChange={(e) => handleInputChange('age', e.target.value)} placeholder="Enter your age" min="0" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sex</label>
          <Select value={formData.sex} onChange={(e) => handleInputChange('sex', e.target.value)}>
            <option value="">Select sex</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="intersex">Intersex</option>
            <option value="other">Other</option>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Height ({heightUnitLabel})</label>
          <Input type="number" value={formData.height} onChange={(e) => handleInputChange('height', e.target.value)} placeholder={`Enter your height in ${heightUnitLabel}`} min="0" step="0.1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weight ({weightUnitLabel})</label>
          <Input type="number" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} placeholder={`Enter your weight in ${weightUnitLabel}`} min="0" step="0.1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ethnicity</label>
          <Input type="text" value={formData.ethnicity} onChange={(e) => handleInputChange('ethnicity', e.target.value)} placeholder="Enter your ethnicity" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
          <Input type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} placeholder="Enter your location" />
        </div>
      </div>
    </div>
  );

  const renderArrayField = (field, label, placeholder) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      {formData[field].map((item, index) => (
        <div key={index} className="flex gap-2 mb-2">
          <Input value={item} onChange={(e) => handleArrayChange(field, index, e.target.value)} placeholder={placeholder} />
          <Button type="button" variant="destructive" size="sm" onClick={() => removeArrayItem(field, index)}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem(field)} className="flex items-center">
        <PlusIcon className="w-4 h-4 mr-2" />
        Add {label.replace(/s$/, '')}
      </Button>
    </div>
  );

  const renderMedicalTab = () => (
    <div className="space-y-6">
      {renderArrayField('chronic_conditions', 'Chronic Conditions', 'Enter chronic condition')}
      {renderArrayField('medications', 'Medications', 'Enter medication')}
      {renderArrayField('allergies', 'Allergies', 'Enter allergy')}
    </div>
  );

  const renderLifestyleTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dietary Preferences</label>
        <Textarea value={formData.dietary_preferences} onChange={(e) => handleInputChange('dietary_preferences', e.target.value)} placeholder="Describe your dietary preferences" rows={3} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Activity Level</label>
        <Select value={formData.activity_level} onChange={(e) => handleInputChange('activity_level', e.target.value)}>
          <option value="">Select activity level</option>
          <option value="sedentary">Sedentary</option>
          <option value="light">Light</option>
          <option value="moderate">Moderate</option>
          <option value="high">High</option>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sleep Hours (per night)</label>
        <Input type="number" value={formData.sleep_hours} onChange={(e) => handleInputChange('sleep_hours', e.target.value)} placeholder="Enter average sleep hours" min="0" max="24" step="0.5" />
      </div>
    </div>
  );

  const renderGoalsTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Goal</label>
        <Textarea value={formData.primary_goal} onChange={(e) => handleInputChange('primary_goal', e.target.value)} placeholder="Describe your primary health goal" rows={3} />
      </div>
      <div className="rounded-lg bg-gray-50 dark:bg-surface-dark-raised p-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Numeric targets — weight, calories, sleep and more — now live on your dials on the{' '}
          <a href="/you" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">You page</a>.
          Each dial is where you set the target and watch it move — one place, no more setting it twice.
        </p>
      </div>
    </div>
  );

  const renderMentalHealthTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mood Logs</label>
        {formData.mood_logs.map((log, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <Input type="date" value={log.date} onChange={(e) => handleMoodLogChange(index, 'date', e.target.value)} />
            <Input value={log.mood} onChange={(e) => handleMoodLogChange(index, 'mood', e.target.value)} placeholder="How are you feeling?" />
            <Button type="button" variant="destructive" size="sm" onClick={() => removeMoodLog(index)}>
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addMoodLog} className="flex items-center">
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Mood Log
        </Button>
      </div>
    </div>
  );

  const renderStatsTabContent = () => {
    switch (statsActiveTab) {
      case 'demographics': return renderDemographicsTab();
      case 'medical': return renderMedicalTab();
      case 'lifestyle': return renderLifestyleTab();
      case 'goals': return renderGoalsTab();
      case 'mental-health': return renderMentalHealthTab();
      default: return null;
    }
  };

  // Which section page we're on (/me/:section); default to profile.
  const { section } = useParams();
  const active = ME_SECTIONS.some((s) => s.id === section) ? section : 'profile';

  // ── Loading ──

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface dark:bg-surface-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8 space-y-6 sm:space-y-8">
      {/* Section nav — no page title; the sidebar/nav already says you're in Settings. */}
      <MeSectionNav />

      {/* Profile Section */}
      {active === 'profile' && (
      <Card id="profile" className="scroll-mt-32 md:scroll-mt-20">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="flex flex-col items-center space-y-3">
              <div className="w-24 h-24 rounded-full bg-solar-gradient shadow-glow-sm flex items-center justify-center text-[#1a1200] text-3xl font-semibold overflow-hidden">
                {getProfilePictureUrl ? (
                  <img src={getProfilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span>{user?.email?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex space-x-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center">
                  <CameraIcon className="w-4 h-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                {user?.profile_picture && (
                  <Button variant="destructive" size="sm" onClick={handleDeleteProfilePicture} className="flex items-center">
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{user?.email || '—'}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Member since {formattedCreatedAt}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Connected Devices */}
      {active === 'devices' && (
      <Card id="devices" className="scroll-mt-32 md:scroll-mt-20">
        <CardHeader>
          <CardTitle>Connected Devices</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {fitbitStatus.connected ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                ) : (
                  <LinkIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Fitbit</p>
                {fitbitStatus.connected ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Connected {formatConnectedDate(fitbitStatus.connected_at)}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not connected</p>
                )}
              </div>
            </div>
            <div>
              {loadingFitbit ? (
                <Button variant="outline" size="sm" disabled>Loading...</Button>
              ) : fitbitStatus.connected ? (
                <div className="flex flex-col items-stretch gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={handleSyncFitbit}
                    disabled={syncingFitbit}
                  >
                    {syncingFitbit ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={handleDisconnectFitbit}
                    disabled={disconnecting}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={handleConnectFitbit}>Connect</Button>
              )}
            </div>
          </div>

          {/* Generic device-layer rows. A provider we can service shows Connect/
              Disconnect; one we can't yet (no server keys, e.g. WHOOP) shows
              "Request support" so a user can register demand instead of hitting a
              dead button. */}
          {genericDevices.map(({ key, label, desc, status, loading, refetch }) => (
            <div key={key} className="flex items-center justify-between p-4 mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {status.connected ? (
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                  ) : (
                    <LinkIcon className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {status.connected ? `Connected ${formatConnectedDate(status.connected_at)}` : desc}
                  </p>
                </div>
              </div>
              <div>{renderDeviceAction(key, status, loading, refetch)}</div>
            </div>
          ))}

          {/* Not-yet-connectable devices people own — a demand signal, not a dead
              connect button. */}
          {requestableDevices.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between p-4 mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <LinkIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
              <div>
                {requestedProviders.includes(key) ? (
                  <Button variant="outline" size="sm" disabled>Requested ✓</Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => handleRequestSupport(key)}>
                    Request support
                  </Button>
                )}
              </div>
            </div>
          ))}

          {/* Android-native only: Health Connect is a push-from-app source
              (no OAuth). Renders nothing on web/iOS. */}
          <HealthConnectRow />
        </CardContent>
      </Card>
      )}

      {/* Preferences */}
      {active === 'preferences' && (
      <Card id="preferences" className="scroll-mt-32 md:scroll-mt-20">
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimezoneCombobox />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weight Unit</label>
            <div className="flex gap-2">
              {['kg', 'lb'].map((u) => (
                <button
                  key={u}
                  onClick={() => handleWeightUnitChange(u)}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    (user?.weight_unit || 'kg') === u
                      ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
            <div className="flex gap-2">
              {[['light', 'Light'], ['dark', 'Dark']].map(([val, lbl]) => (
                <button
                  key={val}
                  onClick={() => { if (theme !== val) toggleTheme(); }}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    theme === val
                      ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Personal Health Profile */}
      {active === 'health' && (
      <Card id="health" className="scroll-mt-32 md:scroll-mt-20">
        <CardHeader>
          <CardTitle>Personal Health Profile</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This information helps personalize your health insights. Fill in what
            you can — a partial profile is fine, and every field sharpens the picture.
            You can also just tell the logger on your home page ("I'm 34", "my goal is
            to sleep better") and it'll fill these in for you.
          </p>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700 mb-6">
                {statsTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setStatsActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      statsActiveTab === tab.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleStatsSubmit}>
                {renderStatsTabContent()}
                <div className="mt-8 flex justify-center">
                  <Button type="submit" disabled={statsSaving} className="px-8 py-3">
                    {statsSaving ? 'Saving...' : stats ? 'Update Stats' : 'Create Stats'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Daily logging checklist */}
      {active === 'checklist' && (
      <section id="checklist" className="scroll-mt-32 md:scroll-mt-20">
        <ChecklistEditor />
      </section>
      )}

      {/* Log reminders (push notifications) */}
      {active === 'reminders' && (
      <section id="reminders" className="scroll-mt-32 md:scroll-mt-20">
        <ReminderSettings />
      </section>
      )}

      {/* Account Actions */}
      {active === 'account' && (
      <Card id="account" className="scroll-mt-32 md:scroll-mt-20">
        <CardContent className="p-6 space-y-6">
          <Button variant="outline" onClick={logout} className="text-red-600 hover:text-red-700 dark:text-red-400">
            Sign Out
          </Button>

          <div className="pt-6 border-t border-gray-200 dark:border-surface-dark-border">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Delete account
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Permanently erase your account and all associated data — health history,
              targets, connected devices, and reminders. This cannot be undone.
            </p>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
              className="mt-3"
            >
              Delete my account
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Delete-account confirmation (theme-aware; the shared Modal is light-only) */}
      <Dialog open={showDeleteModal} onClose={closeDeleteModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-surface-dark-border dark:bg-surface-dark-raised">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Delete your account?
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This permanently deletes your account and <strong>all</strong> of your data —
              health history, targets, connected devices, and reminders. This action cannot
              be undone.
            </Dialog.Description>

            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Type <span className="font-mono font-semibold">DELETE</span> to confirm
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  autoCapitalize="none"
                  disabled={deleting}
                />
              </div>
              {deleteRequiresPassword && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Enter your password
                  </label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Your password"
                    autoComplete="current-password"
                    disabled={deleting}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={!canConfirmDelete}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

    </div>
  );
};

export default MePage;
